import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Input } from "@design-system/Input/Input";
import styles from "./EditableTable.module.scss";

import { EditableTableCell, EditableTableCellProps } from "./EditableTableCell";
import { calculateCellBorders } from "./EditableTableCell.lib";

interface EditableTableCellStringProps extends Omit<
    EditableTableCellProps,
    "children"
> {
    onChange: undefined | ((value: string) => void);
    value: string;
    readOnly?: boolean;
    persistOnBlur?: boolean;
    disabledReason?: string;
}

export const EditableTableCellString = React.memo(
    ({
        id,
        isFirstRow,
        onChange,
        selectionState,
        value,
        onClick,
        className,
        readOnly,
        persistOnBlur,
        disabledReason,
    }: EditableTableCellStringProps) => {
        const cellBorders = calculateCellBorders(selectionState);

        const [localValue, setLocalValue] = useState(value);
        const isFocused = useRef(false);
        const handleChange = useCallback(
            (newValue: string) => {
                setLocalValue(newValue);
                if (!persistOnBlur) {
                    onChange?.(newValue);
                }
            },
            [onChange, persistOnBlur],
        );

        const handleFocus = useCallback(() => {
            isFocused.current = true;
            onClick?.();
        }, [onClick]);

        const handleBlur = useCallback(() => {
            if (persistOnBlur) {
                onChange?.(localValue);
            }
            isFocused.current = false;
        }, [onChange, localValue, persistOnBlur]);

        useEffect(() => {
            if (!isFocused.current) {
                setLocalValue(value);
            }
        }, [value]);

        const cell = (
            <EditableTableCell
                id={id}
                isFirstRow={isFirstRow}
                selectionState={selectionState}
                className={className}
            >
                <Input
                    variant="ghost"
                    className={styles.editableTableInput}
                    style={cellBorders}
                    value={localValue}
                    onChange={(event) => handleChange(event.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    readOnly={readOnly}
                />
            </EditableTableCell>
        );

        if (!disabledReason) {
            return cell;
        }

        return <Tooltip content={disabledReason}>{cell}</Tooltip>;
    },
);
